import { useState } from "react";
import SlideWelcome from "../slides/SlideWelcome";
import SlideWhoWeAre from "../slides/SlideWhoWeAre";
import SlideMarketReality from "../slides/SlideMarketReality";
import SlideFourPillars from "../slides/SlideFourPillars";
import SlideFast from "../slides/SlideFast";
import SlideLean from "../slides/SlideLean";
import SlidePersonal from "../slides/SlidePersonal";
import SlideIntelligent from "../slides/SlideIntelligent";
import SlideStack from "../slides/SlideStack";
import SlideClients from "../slides/SlideClients";
import SlideCommercialModel from "../slides/SlideCommercialModel";
import SlideExploreDemos from "../slides/SlideExploreDemos";
import SlideCathayTeam from "../slides/SlideCathayTeam";
import SlideCathayServices from "../slides/SlideCathayServices";
import SlideCathayCreativity from "../slides/SlideCathayCreativity";
import SlideCathayCommercials from "../slides/SlideCathayCommercials";
import SlideRBATeam from "../slides/SlideRBATeam";
import SlideRBAServices from "../slides/SlideRBAServices";
import SlideRBACreativity from "../slides/SlideRBACreativity";
import SlideRBACommercials from "../slides/SlideRBACommercials";
import SlideSQTeam from "../slides/SlideSQTeam";
import SlideSQServices from "../slides/SlideSQServices";
import SlideSQCreativity from "../slides/SlideSQCreativity";
import SlideSQCommercials from "../slides/SlideSQCommercials";
import PalObjectives from "../slides/pal/PalObjectives";
import PalOpportunity from "../slides/pal/PalOpportunity";
import PalTeam from "../slides/pal/PalTeam";
import PalServices from "../slides/pal/PalServices";
import PalDelivery from "../slides/pal/PalDelivery";
import PalMetadata from "../slides/pal/PalMetadata";
import PalContent from "../slides/pal/PalContent";
import PalAdvertising from "../slides/pal/PalAdvertising";
import PalCommercials from "../slides/pal/PalCommercials";
import PalNextSteps from "../slides/pal/PalNextSteps";
import BrandCarousel from "../shared/BrandCarousel";
import OverlayWrapper from "../shared/OverlayWrapper";
import { airlineLogos } from "../data/contentSources";
import { useTheme } from "../context/ThemeContext";
import { useAirline } from "../context/AirlineContext";
import { Globe, Users, Building2, ChevronLeft, ChevronRight } from "lucide-react";

const AEROPLAY_DARK =
  "https://customer-assets.emergentagent.com/job_24108f3e-b858-4890-993f-f1e1a60b89e6/artifacts/h1sxdkcz_Aeroplay%20logo%202026_White.png";

const slideComponentMap = {
  welcome: (airline) => <SlideWelcome airline={airline} />,
  "who-we-are": () => <SlideWhoWeAre />,
  problem: () => <SlideMarketReality />,
  pillars: () => <SlideFourPillars />,
  fast: () => <SlideFast />,
  lean: () => <SlideLean />,
  personal: (airline) => <SlidePersonal airline={airline} />,
  intelligent: () => <SlideIntelligent />,
  stack: () => <SlideStack />,
  commercial: () => <SlideCommercialModel />,
  demos: () => <SlideExploreDemos />,
  clients: () => <SlideClients />,
  "cathay-team": () => <SlideCathayTeam />,
  "cathay-services": () => <SlideCathayServices />,
  "cathay-creativity": () => <SlideCathayCreativity />,
  "cathay-commercials": () => <SlideCathayCommercials />,
  "rba-team": () => <SlideRBATeam />,
  "rba-services": () => <SlideRBAServices />,
  "rba-creativity": () => <SlideRBACreativity />,
  "rba-commercials": () => <SlideRBACommercials />,
  "sq-team": () => <SlideSQTeam />,
  "sq-services": () => <SlideSQServices />,
  "sq-creativity": () => <SlideSQCreativity />,
  "sq-commercials": () => <SlideSQCommercials />,
  "pal-objectives": () => <PalObjectives />,
  "pal-opportunity": () => <PalOpportunity />,
  "pal-team": () => <PalTeam />,
  "pal-services": () => <PalServices />,
  "pal-delivery": () => <PalDelivery />,
  "pal-metadata": () => <PalMetadata />,
  "pal-content": () => <PalContent />,
  "pal-advertising": () => <PalAdvertising />,
  "pal-commercials": () => <PalCommercials />,
  "pal-next-steps": () => <PalNextSteps />,
};

const groupCompanies = [
  { name: "Aeroplay Entertainment", logo: AEROPLAY_DARK, highlighted: true, tags: ["CSP", "NucleusOS", "AI Curation", "Studio-to-Seat", "Follow-the-sun", "TPN Gold", "Hollywood"] },
  { name: "Silk Route Entertainment", logo: "/assets/Silk_Route_Negative.png", highlighted: true, tags: ["South Asian Content", "Middle East", "Central Asia", "Licensing"] },
  { name: "Neutral Digital", logo: "/assets/Neutral_Negative.png", highlighted: true, tags: ["IFE Design", "GUI/UX", "Brand Systems", "Immersive", "XR/AR"] },
  { name: "APX Luxury", logo: "https://customer-assets.emergentagent.com/job_24108f3e-b858-4890-993f-f1e1a60b89e6/artifacts/vexnnnll_buradaluxury-white.png", highlighted: true, heightScale: 0.56, tags: ["Premium Media", "Prestige Asia", "Celebrity Access", "HK Editorial"] },
  { name: "Maxposure", logo: "/assets/Maxposure_Negative.png", highlighted: false, tags: ["Inflight Media", "Magazines", "Digital Content", "India"] },
  { name: "AeroHub", logo: "/assets/Aerohub_Negative.png", highlighted: false, tags: ["Content Processing", "Encoding", "TPN Gold", "Delivery"] },
  { name: "Alpha Pictures", logo: "/assets/Alpha_Negative.png", highlighted: false, tags: ["Film Production", "Distribution", "Original Content"] },
];

// Slides that render their own hero (edge to edge). All others receive the
// standard editorial layout container.
const FULL_BLEED_IDS = new Set(["welcome"]);

const CenterPanel = ({ currentSlideId, slideList }) => {
  const { isDark } = useTheme();
  const { airline } = useAirline();
  const airlineId = airline?.id || "default";
  const [activeOverlay, setActiveOverlay] = useState(null);

  const currentSlide = slideList[currentSlideId];
  const slideId = currentSlide?.id || "welcome";
  const componentFn = slideComponentMap[slideId];
  const component = componentFn ? componentFn(airline) : null;

  const isFullBleed = FULL_BLEED_IDS.has(slideId);
  const isWhoWeAre = slideId === "who-we-are";

  const canvasStyle = {
    backgroundColor: isDark ? "#0b0b0d" : "var(--k-canvas)",
    color: isDark ? "var(--k-ink-invert)" : "var(--k-ink)",
  };

  // Airline chip pinned bottom left — but only on non-welcome slides (welcome
  // already carries a large "For {airline}" chip in its hero, so a second one
  // is redundant and overlaps the chapters band).
  const AirlineChip = () =>
    airline?.pitchTitle && !isFullBleed ? (
      <div
        data-testid="airline-context-chip"
        className={`absolute bottom-6 left-6 z-10 flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full text-[12px] font-medium ${
          isDark ? "bg-white/[0.07] text-white/70 border border-white/10" : "bg-black/[0.04] text-black/70 border border-black/10"
        }`}
      >
        {airline.brandColor && (
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: airline.brandColor }} />
        )}
        {airline.pitchTitle}
      </div>
    ) : null;

  // Prev / next dots — bottom right
  const isFirst = currentSlideId === 0;
  const isLast = currentSlideId === slideList.length - 1;

  return (
    <div className="h-full flex flex-col relative overflow-hidden" style={canvasStyle}>
      <div
        key={`${slideId}-${airlineId}`}
        className="flex-1 min-h-0 overflow-y-auto"
        style={{ animation: "fadeUp 0.45s cubic-bezier(0.22, 1, 0.36, 1)" }}
      >
        {isFullBleed ? (
          <div className="min-h-full flex items-center" data-testid={`slide-content-${slideId}`}>
            {component}
          </div>
        ) : (
          <div className={`mx-auto max-w-[1220px] px-8 lg:px-12 py-8 lg:py-10 ${airline?.slideOrder ? "pb-28 lg:pb-28" : ""}`} data-testid={`slide-content-${slideId}`}>
            {component}

            {isWhoWeAre && (
              <>
                <div className={`mt-14 pt-8 border-t flex flex-wrap gap-3 ${isDark ? "border-white/10" : "border-black/10"}`}>
                  <PillButton isDark={isDark} onClick={() => setActiveOverlay("map")} icon={Globe} testid="open-world-map">
                    Our global presence
                  </PillButton>
                  <PillButton isDark={isDark} onClick={() => setActiveOverlay("customers")} icon={Users} testid="open-customers">
                    Customers
                  </PillButton>
                  <PillButton isDark={isDark} onClick={() => setActiveOverlay("companies")} icon={Building2} testid="open-companies">
                    Our companies
                  </PillButton>
                </div>
                <div className="mt-10">
                  <BrandCarousel />
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Slide navigation dots — bottom center */}
      {!isFullBleed && (
        <div
          className={`absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-1 px-2 py-1.5 rounded-full backdrop-blur ${
            isDark ? "bg-white/[0.06] border border-white/[0.08]" : "bg-black/[0.04] border border-black/[0.06]"
          }`}
        >
          <button
            data-testid="slide-prev"
            disabled={isFirst}
            onClick={() => window.dispatchEvent(new KeyboardEvent("keydown", { key: "ArrowLeft" }))}
            className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
              isDark ? "text-white/70 hover:bg-white/10" : "text-black/60 hover:bg-black/5"
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className={`px-2 text-[12px] tabular-nums ${isDark ? "text-white/60" : "text-black/60"}`}>
            {currentSlideId + 1} / {slideList.length}
          </span>
          <button
            data-testid="slide-next"
            disabled={isLast}
            onClick={() => window.dispatchEvent(new KeyboardEvent("keydown", { key: "ArrowRight" }))}
            className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
              isDark ? "text-white/70 hover:bg-white/10" : "text-black/60 hover:bg-black/5"
            }`}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      <AirlineChip />

      {activeOverlay === "map" && (
        <OverlayWrapper isDark={isDark} onClose={() => setActiveOverlay(null)} icon={Globe} title="Our Global Presence" subtitle="With our heart in Asia">
          <div style={{ backgroundColor: isDark ? "#0b0b0d" : "#1d1d1f" }}>
            <img src="https://customer-assets.emergentagent.com/job_24108f3e-b858-4890-993f-f1e1a60b89e6/artifacts/1c9d2u11_svgviewer-png-output.png" alt="Global Presence Map" className="w-full h-auto object-contain" />
          </div>
        </OverlayWrapper>
      )}

      {activeOverlay === "customers" && (
        <OverlayWrapper isDark={isDark} onClose={() => setActiveOverlay(null)} icon={Users} title="Customers" subtitle="Trusted by leading airlines worldwide">
          <div className="p-8">
            <div className="grid grid-cols-3 gap-5">
              {airlineLogos.map((al) => (
                <div key={al.name} className={`rounded-xl border overflow-hidden flex items-center justify-center p-4 ${isDark ? "bg-white/[0.03] border-white/10" : "bg-white border-black/10"}`} style={{ aspectRatio: "16/7" }}>
                  <img src={al.logo} alt={al.name} className={`max-w-full max-h-full object-contain ${isDark ? "" : "invert"}`} />
                </div>
              ))}
            </div>
          </div>
        </OverlayWrapper>
      )}

      {activeOverlay === "companies" && (
        <OverlayWrapper isDark={isDark} onClose={() => setActiveOverlay(null)} icon={Building2} iconColor="#F2A900" title="Our Companies" subtitle="A unique ecosystem of specialised media and technology companies" large>
          <div className="p-6">
            <div className="flex gap-4">
              {(() => {
                const aero = groupCompanies.find((c) => c.name === "Aeroplay Entertainment");
                return (
                  <div className={`w-56 flex-shrink-0 rounded-xl p-5 border-2 flex flex-col items-center ${
                    isDark ? "bg-[#F2A900]/5 border-[#F2A900]/30" : "bg-[#F2A900]/5 border-[#F2A900]/20"
                  }`}>
                    <img src={aero.logo} alt={aero.name} className={`h-10 w-auto object-contain mb-4 ${!isDark ? "invert" : ""}`} />
                    <div className={`text-base font-bold mb-3 text-center ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>{aero.name}</div>
                    <div className="flex flex-wrap gap-1.5 justify-center">
                      {aero.tags.map((tag, j) => (
                        <span key={j} className={`text-[10px] px-2 py-0.5 rounded-full ${isDark ? "bg-[#F2A900]/15 text-[#F2A900]" : "bg-[#F2A900]/15 text-[#8B6600]"}`}>{tag}</span>
                      ))}
                    </div>
                  </div>
                );
              })()}
              <div className="flex-1 grid grid-cols-3 gap-3">
                {groupCompanies.filter((c) => c.name !== "Aeroplay Entertainment").map((co, i) => (
                  <div key={i} className={`rounded-xl p-4 border flex flex-col items-center ${isDark ? "bg-white/[0.03] border-white/10" : "bg-black/[0.02] border-black/10"}`}>
                    <img src={co.logo} alt={co.name} className={`w-auto object-contain mb-3 ${!isDark ? "invert" : ""}`} style={{ height: `${(co.heightScale || 1) * 32}px` }} />
                    <div className={`text-sm font-bold mb-2 text-center ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>{co.name}</div>
                    <div className="flex flex-wrap gap-1.5 justify-center">
                      {co.tags.map((tag, j) => (
                        <span key={j} className={`text-[10px] px-2 py-0.5 rounded-full ${isDark ? "bg-white/10 text-white/70" : "bg-black/[0.06] text-black/60"}`}>{tag}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </OverlayWrapper>
      )}
    </div>
  );
};

const PillButton = ({ isDark, onClick, icon: Icon, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`inline-flex items-center gap-2 px-4 h-10 rounded-full text-[14px] font-medium transition-colors ${
      isDark
        ? "bg-white/[0.06] text-white/85 border border-white/10 hover:bg-white/[0.10]"
        : "bg-black/[0.04] text-[#1d1d1f] border border-black/10 hover:bg-black/[0.07]"
    }`}
  >
    <Icon className="w-4 h-4" />
    {children}
  </button>
);

export default CenterPanel;
